import React, { useRef } from "react";
import { Box, Skeleton, Typography } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { ResponsiveBar } from "@nivo/bar";
import { commaNotation, formatPercent } from "@utils";
import HyroHoverTooltip from "@hyro-components/hyro-hover-tooltip/HyroHoverTooltip";
import Tooltip from "@hyro-components/Tooltip";
import QuestionIcon from "src/icons/insights/QuestionIcon";
import typography from "src/theme/typography";

const TOOLTIP_BAR_GAP = 14;
const BELOW_LABEL_OFFSET = 35;
const MIN_BAR_HEIGHT = 16;
const ZERO_BAR_HEIGHT = 6;

const roundedTopRectPath = (x, y, width, height, radius) => {
  const r = Math.min(radius, height, width / 2);
  return `M${x},${y + height} V${y + r} Q${x},${y} ${x + r},${y} H${x + width - r} Q${x + width},${y} ${x + width},${y + r} V${y + height} Z`;
};

const formatTick = (value) => {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1).replace(/\.0$/, "")}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(1).replace(/\.0$/, "")}k`;
  return String(value);
};

const NICE_STEPS = [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10];

const niceTickStep = (value) => {
  if (value <= 0) return 1;
  const rawStep = value / 4;
  const power = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const nice = NICE_STEPS.find((n) => n >= (rawStep / power) - 1e-9) ?? 10;
  return Math.max(1, Math.round(nice * power));
};

const TICK_LABEL_WIDTH = 260;

// Bottom-axis label (name + info tooltip), rendered as HTML inside the SVG so the tooltip can be interactive.
const AxisTickLabel = ({ value, tooltip }) => {
  const theme = useTheme();
  return (
    <foreignObject
      x={-TICK_LABEL_WIDTH / 2}
      y={0}
      width={TICK_LABEL_WIDTH}
      height={28}
      style={{ overflow: "visible", pointerEvents: "auto" }}
    >
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 0.5 }}>
        <Typography variant="subtitle2" sx={{ color: theme.palette.text[800], whiteSpace: "nowrap" }}>{value}</Typography>
        <Tooltip
          variant="light"
          testId={`axis-tooltip-${value}`}
          title={<Typography variant="body2">{tooltip}</Typography>}
          tooltipStyle={{ maxWidth: "330px" }}
          popperStyle={{ marginBottom: "8px" }}
        >
          <Box data-testid={`axis-tooltip-${value}-icon`} sx={{ width: 16, height: 16, display: "flex" }}>
            <QuestionIcon />
          </Box>
        </Tooltip>
      </Box>
    </foreignObject>
  );
};

// 0-data: a thin "disabled" grey bar on the baseline with a grey label above it, so the group stays visible.
const ZeroBar = ({ bar, label }) => {
  const theme = useTheme();
  const zeroY = (bar.y + bar.height) - ZERO_BAR_HEIGHT;
  return (
    <g>
      <path d={roundedTopRectPath(bar.x, zeroY, bar.width, ZERO_BAR_HEIGHT, 4)} fill={theme.palette.text[600]} />
      <text
        x={bar.x + (bar.width / 2)}
        y={zeroY - 8}
        textAnchor="middle"
        style={{
          fill: theme.palette.text[600],
          fontFamily: "inter,sans-serif",
          fontSize: 14,
          letterSpacing: "0.1px"
        }}
      >
        {label}
      </text>
    </g>
  );
};

const HyroBarChart = ({ data = [], height = 400, loading = false, showTrack = true, padding = 0.5, total: totalProp, renderTooltip: renderTooltipProp, tooltipBelowLabel = false, showZeroBars = false, onBarClick }) => {
  const theme = useTheme();
  const hoverTooltipRef = useRef(null);
  const chartRef = useRef(null);

  if (loading) {
    return (
      <Box sx={{ height, width: "100%" }}>
        <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: 1 }} />
      </Box>
    );
  }

  const total = totalProp ?? data[0]?.value ?? 0;
  const chartData = data.map((d) => ({ ...d, total }));
  const dataMax = Math.max(0, ...data.map((d) => d.value || 0));
  const step = niceTickStep(dataMax);
  const yMax = step * 4;
  const yTickValues = Array.from({ length: 5 }, (_, i) => step * i);
  const tickTooltips = Object.fromEntries(data.filter((d) => d.tooltip).map((d) => [d.label, d.tooltip]));

  const defaultRenderTooltip = (datum) => (
    <BarTooltip>
      <strong>{datum.label}</strong>
      <span>{commaNotation(datum.value)} ({formatPercent(datum.value, datum.total)})</span>
    </BarTooltip>
  );

  const barTooltipModifiers = ({ boundary }) => [
    {
      name: "offset",
      options: { offset: ({ placement }) => (placement.startsWith("top") ? [0, TOOLTIP_BAR_GAP] : [0, 0]) }
    },
    tooltipBelowLabel
      ? { name: "flip", options: { fallbackPlacements: ["top"], boundary } }
      : { name: "flip", enabled: false }
  ];

  const TrackLayer = ({ bars, yScale }) => {
    const trackTop = yScale(total);
    const trackBottom = yScale(0);
    const trackHeight = Math.max(0, trackBottom - trackTop);
    return (
      <g>
        {bars.map((bar) => (
          <rect
            key={`track-${bar.data.indexValue}`}
            x={bar.x}
            y={trackTop}
            width={bar.width}
            height={trackHeight}
            rx={4}
            fill={theme.palette.info[200]}
            style={{ pointerEvents: "none" }}
          />
        ))}
      </g>
    );
  };

  const InteractionLayer = ({ bars, yScale }) => {
    const trackTop = yScale(total);
    const trackBottom = yScale(0);
    const trackHeight = Math.max(0, trackBottom - trackTop);

    const showTooltip = (event, bar) => {
      const hit = event.currentTarget.getBoundingClientRect();
      // nivo drops the plotted key from bar.data.data when the value is 0, so read the row from chartData
      const datum = chartData.find((d) => d.label === bar.data.indexValue) ?? bar.data.data;
      const zeroBarTop = showTrack ? trackTop : trackBottom - ZERO_BAR_HEIGHT;
      const barTopSvg = datum.value > 0 ? bar.y : zeroBarTop;
      const top = hit.bottom - (trackBottom - barTopSvg);
      hoverTooltipRef.current?.show(
        datum,
        new DOMRect(hit.left, top, hit.width, tooltipBelowLabel ? BELOW_LABEL_OFFSET : 0),
        // the chart element, not the hovered <rect>: popper sizes a boundary from
        // clientWidth/clientHeight, which are always 0 on an SVG child
        { boundary: chartRef.current }
      );
    };

    return (
      <g>
        {bars.map((bar) => (
          <rect
            key={`hover-${bar.data.indexValue}`}
            data-testid={`bar-hit-${bar.data.indexValue}`}
            x={bar.x}
            y={trackTop}
            width={bar.width}
            height={trackHeight}
            fill="transparent"
            style={{ pointerEvents: "all", cursor: onBarClick ? "pointer" : "default" }}
            onMouseEnter={(event) => showTooltip(event, bar)}
            onMouseLeave={() => hoverTooltipRef.current?.hide()}
            onClick={() => onBarClick?.(bar.data.data)}
          />
        ))}
      </g>
    );
  };

  // Custom bar: Nivo has no minBarHeight, so we enforce a 16px floor and always render the label.
  const BarItem = ({ bar, label }) => {
    const hasValue = bar.data.value > 0;

    if (!hasValue) {
      if (!showZeroBars) return null;
      return <ZeroBar bar={bar} label={label} />;
    }

    const barHeight = Math.max(bar.height, MIN_BAR_HEIGHT);
    const barY = (bar.y + bar.height) - barHeight;
    const compact = barHeight < 32;
    const labelHeight = compact ? 14 : 17;
    // 17px from the top when the bar fits 17px above and below the label, otherwise vertically centered.
    const anchorTop = barHeight - (2 * 17) >= labelHeight;
    return (
      <g>
        <path d={roundedTopRectPath(bar.x, barY, bar.width, barHeight, 4)} fill={bar.color} />
        <text
          x={bar.x + (bar.width / 2)}
          y={anchorTop ? barY + 17 : barY + (barHeight / 2)}
          textAnchor="middle"
          dominantBaseline={anchorTop ? "hanging" : "central"}
          style={{
            fill: theme.palette.system[0],
            fontFamily: "inter,sans-serif",
            fontSize: compact ? 11 : 14,
            fontWeight: compact ? 500 : 400,
            letterSpacing: compact ? 0 : "0.1px"
          }}
        >
          {label}
        </text>
      </g>
    );
  };

  // Rich HTML label (name + info tooltip) when a tooltip description is provided for the bar; otherwise a plain SVG text label.
  const renderAxisTick = ({ x, y, value }) => {
    const tooltip = tickTooltips[value];
    return (
      <g transform={`translate(${x},${y + 16})`}>
        {tooltip ? (
          <AxisTickLabel value={value} tooltip={tooltip} />
        ) : (
          <text
            textAnchor="middle"
            dominantBaseline="hanging"
            style={{ ...typography.subtitle2, fill: theme.palette.text[800] }}
          >
            {value}
          </text>
        )}
      </g>
    );
  };

  const layers = ["grid", "axes"];
  if (showTrack) layers.push(TrackLayer);
  layers.push("bars", "markers", "legends", InteractionLayer);

  return (
    <Box ref={chartRef} sx={{ height, width: "100%" }}>
      <ResponsiveBar
        data={chartData}
        keys={["value"]}
        indexBy="label"
        margin={{ top: 16, right: 0, bottom: 40, left: 40 }}
        padding={padding}
        valueScale={{ type: "linear", min: 0, max: yMax }}
        indexScale={{ type: "band", round: true }}
        colors={(bar) => bar.data?.color ?? theme.palette.secondary[500]}
        barComponent={BarItem}
        enableLabel
        label={(d) => `${commaNotation(d.value)} (${formatPercent(d.value, d.data?.data?.total ?? total)})`}
        axisTop={null}
        axisRight={null}
        axisBottom={{
          tickSize: 0,
          tickPadding: 16,
          renderTick: renderAxisTick
        }}
        axisLeft={{ tickSize: 0, tickPadding: 8, tickValues: yTickValues, format: formatTick }}
        enableGridY
        gridYValues={yTickValues}
        enableGridX={false}
        layers={layers}
        isInteractive={false}
        theme={{
          axis: {
            ticks: { text: { fill: theme.palette.text[700], fontSize: 10, fontFamily: "inter,sans-serif" } }
          },
          grid: { line: { stroke: theme.palette.system[200], strokeDasharray: "0" } },
          labels: { text: { fontSize: 14, fontFamily: "inter,sans-serif", letterSpacing: "0.1px" } }
        }}
        animate
        motionConfig="gentle"
      />
      <HyroHoverTooltip
        ref={hoverTooltipRef}
        placement={tooltipBelowLabel ? "bottom" : "top"}
        modifiers={barTooltipModifiers}
        renderContent={(datum) => (renderTooltipProp ?? defaultRenderTooltip)(datum)}
      />
    </Box>
  );
};

const BarTooltip = styled("div")(({ theme }) => ({
  background: theme.palette.system[0],
  padding: theme.spacing(1, 1.5),
  borderRadius: theme.spacing(0.75),
  border: `1px solid ${theme.palette.grey[200]}`,
  boxShadow: theme.shadows[2],
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.25),
  ...theme.typography.caption,
  minWidth: 120,
  color: theme.palette.text[800]
}));

export default HyroBarChart;
